import { type FC, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import InputSelect from '@components/InputSelect';
import { SearchableInputSelect } from '@components/SearchableInputSelect';
import type { UIInputValue } from '@components/types';
import { useBrandingOptions } from '@features/create-entity-modal';
import { useSubmissions } from '@queries/fetchSubmissions';
import type { TemplateShort } from '@queries/fetchTemplates';
import { useTemplates } from '@queries/fetchTemplates';
import { isEmptyValue } from '@root/helpers';
import type { SubmissionShort } from '@src/queries';

import { TEMPLATES_NOT_FOUND_ERROR } from '../constants';
import type { NamedEntity, PublishedTemplate, StepProps } from '../types';

const filterPublishedTemplates = (item: TemplateShort): item is PublishedTemplate => {
  return !isEmptyValue(item.name) && item.status === 'PUBLISHED';
};

const filterSubmissions = (item: SubmissionShort): item is NamedEntity<SubmissionShort> => {
  return !isEmptyValue(item.name) && item.status !== 'PRE_DRAFT';
};

const getOptions = <T extends Pick<TemplateShort, 'name' | 'id'>>(
  data: T[],
  filter: (item: T) => item is NamedEntity<T>,
) => {
  return data.filter(filter).map((item) => ({ name: item.name, value: item.id }));
};

export const Step2: FC<StepProps> = ({ control, formValues, setError, setValue }) => {
  const { brandingFieldName, isLoading: isFetchingBranding, onCopyBrandingFromResource } = useBrandingOptions();
  const errorSetRef = useRef(false);

  const { data: templates = [], isLoading: isFetchingTemplates, status } = useTemplates();

  useEffect(() => {
    if (status === 'success' && templates && !errorSetRef.current) {
      const publishedTemplates = templates.filter(filterPublishedTemplates);
      if (publishedTemplates.length === 0) {
        errorSetRef.current = true;
        setError('copy_from_template', { message: TEMPLATES_NOT_FOUND_ERROR });
      }
    }
  }, [templates, setError, status]);

  const { data: submissions = [], isLoading: isFetchingSubmissions } = useSubmissions();

  const handleCopyFromEntityIdChange = (entityId: UIInputValue, mode: 'submissions' | 'templates') => {
    const data = mode === 'submissions' ? submissions : templates;
    const entity = data.find((item) => item.id === entityId);

    const result = onCopyBrandingFromResource(entity);

    if (result) {
      setValue(brandingFieldName, result);
    }
  };

  const parsedTemplates = getOptions(templates, filterPublishedTemplates);
  const submissionOptions = getOptions(submissions, filterSubmissions);

  const isLoading = isFetchingSubmissions || isFetchingTemplates || isFetchingBranding;

  const disableTemplatesPicker = !!formValues?.copy_from_submission || isLoading;
  const disableSubmissionsPicker = !!formValues?.copy_from_template || isLoading;

  return (
    <div className="flex flex-col">
      <Controller
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputSelect
            {...field}
            errors={error?.message}
            isClearable
            isDisabled={disableTemplatesPicker}
            isLoading={isLoading}
            isSearchable
            menuOuterClassName="max-w-lg"
            onChange={(value) => {
              handleCopyFromEntityIdChange(value, 'templates');
              return field.onChange(value);
            }}
            labelText="Select a template"
            options={parsedTemplates}
            placeholder="Search"
            value={formValues?.copy_from_template}
          />
        )}
        name="copy_from_template"
      />

      <div className="relative flex items-center py-5 text-gray-500">
        <div className="grow border-t border-gray-300"></div>
        <span className="mx-2 shrink text-sm leading-5 font-normal">Or</span>
        <div className="grow border-t border-gray-300"></div>
      </div>

      <Controller
        control={control}
        render={({ field: { ref: _ref, ...props }, fieldState: { error } }) => (
          <SearchableInputSelect
            {...props}
            errors={error?.message}
            isDisabled={disableSubmissionsPicker}
            isLoading={isLoading}
            noOptionsLeftLabel="No existing contracts available"
            onChange={(value) => {
              handleCopyFromEntityIdChange(value, 'submissions');
              return props.onChange(value);
            }}
            labelText="Duplicate an existing contract"
            placeholder="Search contracts"
            options={submissionOptions}
            // Getting the value from `formValues` as opposed to `Controller` since it does a weird caching which breaks the `Clear selection` logic
            value={formValues?.copy_from_submission}
          />
        )}
        name="copy_from_submission"
      />
    </div>
  );
};
